import { FormEvent } from 'react'

import { Control, Controller, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { ICustomerDetails } from 'pages/excursions/customer-details'

import { MplusPayment } from 'widgets/mplus/mplus-payment/ui'
import { MplusUnactive } from 'widgets/mplus/mplus-payment/ui/components/mplus-unactive'

import { orderExcursionsStore } from 'entities/excursions/models/order-excursions/order-excursions-store'

import { EMAIL_REGEX } from 'shared/constants/regex'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import {
  PhoneInputWhitLabel,
  customValidatePhone,
} from 'shared/ui/phone-input-label'

import s from './styles.module.scss'

type Props = {
  control: Control<ICustomerDetails, unknown>
  errors: FieldErrors<ICustomerDetails>
  onOpenMplusPage: () => void
  onShowConform: () => void
  onPurchaseRegular: () => void
  onPurchaseMplus: (dayMplus: number) => void
  onChangeInputEmail: (event: FormEvent<HTMLInputElement>) => string
}

export const CustomerForm = observer(
  ({
    errors,
    control,
    onShowConform,
    onOpenMplusPage,
    onPurchaseMplus,
    onPurchaseRegular,
    onChangeInputEmail,
  }: Props) => {
    const { t } = useTranslation()

    const { people_quantity, total_price, tranche_amount } =
      orderExcursionsStore.orderExcursionInfo

    const onSubmitPurchase = () => {
      onPurchaseRegular()
    }

    const onSubmitPurchaseMplus = (day: number) => {
      onPurchaseMplus(day)
    }

    const mplusRequestAmount = String(
      parseInt(tranche_amount || '1') * people_quantity,
    )

    const confirmComponent = (
      <p className={clsx(s.textBlock, 'regular-12')}>
        {`${t('purchase_page.agree')} `}
        <span
          className={s.underLine}
          onClick={() => onShowConform?.()}
        >
          {t('purchase_page.offers')}
        </span>
        {` ${t('purchase_page.end')}`}
      </p>
    )

    return (
      <>
        <HeaderGoBack
          title={t('excursions.customer_data')}
          buttonWhite
        />
        <div className={s.container}>
          <div className={s.contactWrapper}>
            <Paper padding={[20]}>
              <p className={s.contactTitle}>
                {t('purchase_page.contact_title')}
              </p>

              <div className={s.contactField}>
                <Controller
                  control={control}
                  name="last_name"
                  rules={{ required: true, minLength: 2, maxLength: 30 }}
                  render={({ field }) => (
                    <InputWithLabel
                      ref={field.ref}
                      value={field?.value}
                      label={t('last_name')}
                      error={errors?.last_name}
                      onChange={(event) =>
                        field?.onChange(event.currentTarget.value)
                      }
                    />
                  )}
                />
              </div>
              <div className={s.contactField}>
                <Controller
                  control={control}
                  name="first_name"
                  rules={{ required: true, minLength: 2, maxLength: 30 }}
                  render={({ field }) => (
                    <InputWithLabel
                      ref={field.ref}
                      value={field?.value}
                      label={t('first_name')}
                      error={errors?.first_name}
                      onChange={(event) =>
                        field?.onChange(event.currentTarget.value)
                      }
                    />
                  )}
                />
              </div>

              <div className={s.contactField}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: false,
                    pattern: {
                      value: EMAIL_REGEX,
                      message: '',
                    },
                  }}
                  render={({ field }) => (
                    <InputWithLabel
                      lang={'en'}
                      value={field.value}
                      error={errors?.email}
                      label={t('email')}
                      onChange={(event) =>
                        field.onChange(onChangeInputEmail(event))
                      }
                    />
                  )}
                />
              </div>
              <div className={s.contactField}>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: true,
                    validate: customValidatePhone,
                  }}
                  render={({ field }) => (
                    <PhoneInputWhitLabel
                      label={t('purchase_page.phone_number')}
                      error={errors.phone}
                      value={field?.value}
                      ref={field.ref}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
              </div>
              <p className={s.whatsapp}>{t('excursions.whatsapp')}</p>
            </Paper>
          </div>

          <MplusPayment
            price={String(total_price)}
            countPassengers={people_quantity}
            tranchePrice={mplusRequestAmount}
            confirmComponent={confirmComponent}
            onPayMplus={onSubmitPurchaseMplus}
            onOpenMplus={onOpenMplusPage}
          >
            <MplusUnactive
              price={String(total_price)}
              countPassengers={people_quantity}
              confirmComponent={confirmComponent}
              onSubmit={onSubmitPurchase}
            />
          </MplusPayment>
        </div>
      </>
    )
  },
)
